import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { getProductCapabilities } from "@/business/product-capabilities";
import { useBusiness } from "@/core/business";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { useGetMyUser } from "@/user/query";
import { ProductMarketingPage } from "./product-marketing-page";
import { getMarketingProduct } from "./routes";

export const ProductMarketingBoundary = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { pathname } = useLocation();
  const { businessId } = useBusiness();
  const { settings, isLoading, hasValidSettings } = useBusinessProducts();
  const { data: user } = useGetMyUser();
  const product = getMarketingProduct(pathname);
  const capabilities = getProductCapabilities({ user, settings });
  if (!product || !capabilities.canPreviewProducts) return children;
  if (isLoading)
    return <Spinner layout="centered" aria-label="Loading product access" />;
  if (!hasValidSettings)
    return (
      <Stack align="start" gap={4} className="p-8">
        <Text role="alert" tone="destructive">
          Could not load product settings. Refresh the page and try again.
        </Text>
      </Stack>
    );
  if (capabilities.preview[product])
    return (
      <ProductMarketingPage
        key={`${businessId}-${product}`}
        product={product}
      />
    );
  return children;
};
